/* eslint-disable react/forbid-prop-types */
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { appConfig as config } from 'roc-package-web-app-react/app/shared/universal-config';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { themr } from 'react-css-themr';
import { provideHooks } from 'redial';
import { push as pushLocationHistory } from 'react-router-redux';
import { reduxActions as trackingActions } from '@spp/tracking';
import {
    fetchPlussArticlesAction,
    addPlussArticlesAction,
} from '../../redux/actions/pluss-articles';
import deferOnload from '../../utils/defer-onload';
import initSpid from '../../utils/init-spid';
import GridTeaser from '../../local-components/GridTeaser';
import GenericError from '../error';
import clientParams from '../../tracking/client-params';
import { getFonts, fontsToInlineStyle } from '../../utils/font-chooser';

function mapDispatchToProps(dispatch) {
    return bindActionCreators(
        {
            fetchPlussArticlesAction,
            addPlussArticlesAction,
            pushLocationHistory,
        },
        dispatch,
    );
}

function FilterButton(props) {
    /* eslint-disable react/prop-types */
    return (
        <button className={props.selected} onClick={props.onClick}>
            {props.value}
        </button>
    );
    /* eslint-enable react/prop-types */
}

const ARTICLES_PER_PAGE = 21;

const sections = [
    {
        name: 'Nyheter',
        id: 'addeb271-e473-4fff-adef-32e3b8602605',
        excludedsections: '',
        selected: false,
    },
    {
        name: 'Sport',
        id: 'bd063f91-6bff-4fa1-84c2-a19a66b64d5f',
        excludedSections: '',
        selected: false,
    },
    {
        name: 'Rampelys',
        id: 'd70c055a-3c91-483c-b4b2-8fffcf04f2a6',
        excludedSections: '',
        selected: false,
    },
    {
        name: 'Forbruker',
        id: 'de434f6e-6c77-438e-aa59-639b118247f7',
        excludedSections: '509d9cb5-d17c-43fa-a44d-0c729089a1cb',
        selected: false,
    },
    {
        name: 'Livsstil',
        id: '509d9cb5-d17c-43fa-a44d-0c729089a1cb',
        excludedSections: '',
        selected: false,
    },
];

@provideHooks({
    /* eslint-disable arrow-body-style */
    fetch: ({ dispatch, force, location }) => {
        const { offset = 0, section } = location.query;
        let selectedSectionId;
        let excludedSections;
        if (section) {
            const selectedSection = sections.find((s) => s.name.toLowerCase() === section);
            if (selectedSection) {
                selectedSectionId = selectedSection.id;
                excludedSections = selectedSection.excludedSections;
            }
        }
        return dispatch(
            fetchPlussArticlesAction({
                force,
                offset,
                section: selectedSectionId,
                excludedSections,
            }),
        );
    },
    /* eslint-enable arrow-body-style */
    defer: ({ dispatch, location, getProps }) =>
        dispatch(async (thunkDispatch, getState) => {
            const { layout } = getProps();
            const spidPromise = initSpid({ thunkDispatch, location, getState });

            await deferOnload();

            const trackingOpt = clientParams({
                config,
                layout,
                state: getState(),
            });

            const trackingPromise = thunkDispatch(
                trackingActions.trackerMultipleStart(trackingOpt),
            );

            await Promise.all([spidPromise, trackingPromise]);
        }),
})
@connect(
    (state, ownProps) => ({
        articles: state.plussArticles,
        section: ownProps.location.query.section,
    }),
    mapDispatchToProps,
)
@themr('PlussFront')
export default class PlussFront extends PureComponent {
    static propTypes = {
        theme: PropTypes.object,
        articles: PropTypes.object,
        section: PropTypes.string,
        pushLocationHistory: PropTypes.func.isRequired,
        fetchPlussArticlesAction: PropTypes.func.isRequired,
        addPlussArticlesAction: PropTypes.func.isRequired,
        location: PropTypes.object,
    };

    static defaultProps = {
        theme: {},
        articles: {},
        location: {},
    };

    constructor(props) {
        super(props);

        const { location, articles, section } = props;

        let selectedSection;
        if (section) {
            selectedSection = sections.find((s) => s.name.toLowerCase() === section);
        }

        this.state = {
            offset: location.query.offset || 0,
            selectedSection,
            hasMoreArticles: articles.pluss && articles.pluss.length >= ARTICLES_PER_PAGE,
        };
    }

    componentDidUpdate(prevProps) {
        if (prevProps.section !== this.props.section) {
            const section = this.props.section;
            let selectedSection;
            if (section) {
                selectedSection = sections.find((s) => s.name.toLowerCase() === section);
            }
            this.selectSection(selectedSection);
        }
    }

    handleFilterClick = (i) => {
        let selectedSection = sections[i];
        const { selectedSection: previouslySelectedSection } = this.state;
        if (previouslySelectedSection && previouslySelectedSection.id === selectedSection.id) {
            selectedSection = undefined;
        }

        let path = this.props.location.pathname;
        if (selectedSection) {
            path += `?section=${encodeURIComponent(selectedSection.name.toLowerCase())}`;
        }
        this.props.pushLocationHistory(path);
    };

    selectSection(selectedSection) {
        const section = selectedSection && selectedSection.id;
        const excludedSections = selectedSection && selectedSection.excludedSections;

        this.setState({
            selectedSection,
            offset: 0,
        });

        this.props
            .fetchPlussArticlesAction({
                force: true,
                offset: 0,
                section,
                excludedSections,
            })
            .then((response) => {
                if (
                    !response.payload.articles ||
                    response.payload.articles.length < ARTICLES_PER_PAGE
                ) {
                    this.setState({
                        hasMoreArticles: false,
                    });

                    return;
                }

                window.addEventListener('scroll', this.scrollListener);
            });
    }

    fetchMoreArticles = () => {
        const newOffset = this.state.offset + ARTICLES_PER_PAGE;
        const { selectedSection } = this.state;
        const section = selectedSection && selectedSection.id;
        const excludedSections = selectedSection && selectedSection.excludedSections;

        this.setState({ offset: newOffset });
        this.props
            .addPlussArticlesAction({
                offset: newOffset,
                section,
                excludedSections,
            })
            .then((response) => {
                if (
                    !response.payload.articles ||
                    response.payload.articles.length < ARTICLES_PER_PAGE
                ) {
                    this.setState({
                        hasMoreArticles: false,
                    });

                    return;
                }

                window.addEventListener('scroll', this.scrollListener);
            });
    };

    render() {
        const { theme = {}, articles = {}, location } = this.props;
        const { offset, selectedSection, hasMoreArticles } = this.state;

        if (articles.error || !articles.pluss.length) {
            const statusCode = articles.status || 404;
            return <GenericError statusCode={statusCode} theme={theme} />;
        }

        const selectedSectionId = selectedSection && selectedSection.id;
        const filterButtons = sections.map((filter, index) => (
            <FilterButton
                key={filter.id}
                value={filter.name}
                selected={selectedSectionId === filter.id ? theme.active : ''}
                onClick={() => this.handleFilterClick(index)}
            />
        ));

        const plussArticles = articles.pluss.map((article, index) => (
            <GridTeaser
                key={article.id}
                article={article}
                theme={theme}
                index={index}
                utmParams={{ utm_source: 'pluss' }}
            />
        ));

        const fonts = getFonts(config.fonts);
        const fontPreloadLinks = fonts.map((font) => ({
            rel: 'preload',
            as: 'font',
            type: 'font/woff2',
            href: `${font.cdn}.woff2`,
            crossOrigin: true,
        }));

        const styles = [
            {
                type: 'text/css',
                cssText: fontsToInlineStyle(fonts),
            },
        ];

        const links = [
            {
                rel: 'canonical',
                href: location.pathname,
            },
        ];

        if (hasMoreArticles) {
            links.push({
                rel: 'next',
                href: `${location.pathname}?offset=${offset + ARTICLES_PER_PAGE}`,
            });
        }

        return (
            <div className={theme.root}>
                <Helmet title="VG+" link={links.concat(fontPreloadLinks)} style={styles} />
                <section className={theme.front}>
                    <div className={theme.filterButtons}>
                        <div className={theme.filterIndicator}>Les mer VG+</div>
                        {filterButtons}
                    </div>
                    <div className={theme.articleCards}>{plussArticles}</div>
                    <button className={theme.showMore} onClick={this.fetchMoreArticles}>
                        Vis flere artikler
                    </button>
                </section>
            </div>
        );
    }
}



/** WEBPACK FOOTER **
 ** ./src/screens/front/pluss.js
 **/